import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin, Monitor } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { fetchWorkshops } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const fmtDate = (d) => {
    if (!d) return null;
    try {
        return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return d; }
};

export const FormatBadge = ({ format }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gold-300/40 bg-gold-300/10 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-gold-300">
        {format === "online" ? <Monitor size={10} /> : <MapPin size={10} />}
        {format === "hybrid" ? "Online + Offline" : format}
    </span>
);

export function WorkshopCard({ w }) {
    return (
        <Link
            to={`/workshops/${w.slug}`}
            data-testid={`workshop-card-${w.slug}`}
            className="group glass glow-border block overflow-hidden rounded-lg transition hover:border-gold-300/40"
        >
            <div className="relative aspect-[16/9] overflow-hidden">
                <img
                    src={w.image ? `${BACKEND}${w.image}` : "/images/workshop-hero.jpg"}
                    alt={`${w.title} — business workshop by Abhijit JK`}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute left-4 top-4"><FormatBadge format={w.format} /></div>
                {w.sold_out && (
                    <div className="absolute right-4 top-4 rounded-full bg-red-500/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">Sold Out</div>
                )}
            </div>
            <div className="p-6">
                <h3 className="font-display text-xl font-medium leading-snug">{w.title}</h3>
                {w.headline && <p className="mt-2 text-sm text-white/55">{w.headline}</p>}
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-white/60">
                    {w.start_date && <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} className="text-gold-300" /> {fmtDate(w.start_date)}</span>}
                    {w.duration_label && <span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-gold-300" /> {w.duration_label}</span>}
                    {w.start_time && <span>{w.start_time}{w.end_time ? ` – ${w.end_time}` : ""}</span>}
                    {w.format !== "online" && w.city && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-gold-300" /> {w.city}</span>}
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                    <div>
                        {w.strike_price && <span className="mr-2 text-sm text-white/40 line-through">₹{w.strike_price.toLocaleString("en-IN")}</span>}
                        <span className="gold-text font-display text-xl font-semibold">₹{w.price.base.toLocaleString("en-IN")}</span>
                        {w.price.label !== "Regular" && <span className="ml-2 text-[10px] uppercase tracking-widest text-gold-300/80">{w.price.label}</span>}
                    </div>
                    {typeof w.seats_left === "number" && !w.sold_out && w.seats_left <= 5 && (
                        <span className="text-xs text-gold-300">Only {w.seats_left} seats left</span>
                    )}
                </div>
                <span className="gold-button mt-5 inline-flex w-full items-center justify-center gap-2">
                    {w.sold_out ? "Sold Out" : "Book Your Seat"} <ArrowRight size={13} />
                </span>
            </div>
        </Link>
    );
}

export default function UpcomingWorkshops() {
    const [items, setItems] = useState([]);
    useEffect(() => {
        fetchWorkshops().then((d) => setItems(d.filter((w) => w.phase !== "completed").slice(0, 3))).catch(() => {});
    }, []);
    if (!items.length) return null;
    return (
        <section data-testid="home-workshop-section" className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
            <SectionEyebrow>Upcoming Workshops</SectionEyebrow>
            <h2 className="mt-5 font-display text-3xl font-medium leading-tight sm:text-4xl">
                Learn and grow <span className="gold-text">with your team.</span>
            </h2>
            <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {items.map((w) => <WorkshopCard key={w.id} w={w} />)}
            </div>
            <div className="mt-10">
                <Link to="/workshops" data-testid="home-workshop-cta" className="ghost-button inline-flex items-center gap-2">
                    View All Workshops <ArrowRight size={14} />
                </Link>
            </div>
        </section>
    );
}
